/**
 * Dettaglio ordine di vendita (Fase 2.1 catena documentale).
 * Testata + righe + link al preventivo di origine. UI solo da @floraltech/ui.
 */
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@floraltech/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface Rata {
  id: string;
  numero: number;
  tipo: string;
  descrizione: string | null;
  percentuale: number | string | null;
  importo: number | string | null;
  giorni_scadenza: number;
  fattura_id: string | null;
  fattura_year: number | null;
}

interface Riga {
  id: string;
  descrizione: string;
  quantita: number | string;
  prezzo_unitario: number | string;
  iva_perc: number | string | null;
  sconto_perc: number | string | null;
  importo_riga: number | string;
  // Tipo riga propagato dal preventivo (badge read-only): false = Prodotto.
  is_servizio: boolean;
}

interface AllegatoFirmato {
  documento_id: string;
  year: number;
  nome: string | null;
  tipo: string | null;
  mime_type: string | null;
  created_at: string | null;
}

interface OrdineVendita {
  id: string;
  numero: string;
  anno: number;
  stato: string;
  data_ordine: string;
  cliente_id: string;
  preventivo_id: string | null;
  valuta: string;
  importo_totale: number | string;
  modalita_pagamento: string | null;
  tipo_offerta: string;
  oggetto: string | null;
  note: string | null;
  // Gating della revoca: verita' calcolata server-side.
  revocabile: boolean;
  motivo_non_revocabile: string | null;
  righe: Riga[];
}

const fmtEUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

export function OrdineVenditaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ordine, setOrdine] = useState<OrdineVendita | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState<Rata[]>([]);
  const [nuovaRata, setNuovaRata] = useState({ tipo: 'acconto', percentuale: '', importo: '', giorni_scadenza: '0' });
  const [allegati, setAllegati] = useState<AllegatoFirmato[]>([]);
  const [uploadingFirmato, setUploadingFirmato] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` });

  const caricaRate = async () => {
    if (!id) return;
    const res = await fetch(`/api/v1/ordini-vendita/${id}/rate`, { headers: auth() });
    if (res.ok) setRate(await res.json());
  };

  const caricaAllegati = async () => {
    if (!id) return;
    const res = await fetch(`/api/v1/ordini-vendita/${id}/allegati`, { headers: auth() });
    if (res.ok) setAllegati(await res.json());
  };

  const uploadFirmato = async (file: File) => {
    if (!id) return;
    setUploadingFirmato(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/v1/ordini-vendita/${id}/allegato-firmato`, {
        method: 'POST',
        headers: auth(),
        body: fd,
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.detail ?? `Errore ${res.status}`);
      }
      // Aggiorna solo la lista allegati (non ricarica l'intera pagina: la
      // posizione di scroll e lo stato del resto restano intatti).
      await caricaAllegati();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento ordine firmato');
    } finally {
      setUploadingFirmato(false);
    }
  };

  const downloadAllegato = async (docId: string, year: number, nome: string | null) => {
    try {
      const res = await fetch(`/api/v1/archivio/download/${docId}?year=${year}`, { headers: auth() });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nome ?? 'ordine-firmato';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore download');
    }
  };

  const handleRevoca = async () => {
    if (!id || !ordine) return;
    const conferma = window.confirm(
      `Revocare l'ordine ${ordine.numero}?\n\n` +
        "Il preventivo di origine tornera' in BOZZA (di nuovo modificabile) e il " +
        "numero d'ordine verra' rilasciato se e' l'ultimo della serie.\n\n" +
        "L'azione e' registrata e NON e' reversibile.",
    );
    if (!conferma) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/ordini-vendita/${id}/revoca`, { method: 'POST', headers: auth() });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      // Il preventivo e' tornato editabile: portiamo l'utente li'.
      navigate(`/vendite/preventivi/${data.preventivo_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore revoca ordine');
    } finally {
      setBusy(false);
    }
  };

  const aggiungiRata = async () => {
    if (!id) return;
    setError(null);
    const body: Record<string, unknown> = { tipo: nuovaRata.tipo, giorni_scadenza: Number(nuovaRata.giorni_scadenza) || 0 };
    if (nuovaRata.percentuale) body.percentuale = Number(nuovaRata.percentuale);
    else if (nuovaRata.importo) body.importo = Number(nuovaRata.importo);
    const res = await fetch(`/api/v1/ordini-vendita/${id}/rate`, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setNuovaRata({ tipo: 'acconto', percentuale: '', importo: '', giorni_scadenza: '0' });
      await caricaRate();
    } else {
      const b = await res.json().catch(() => null);
      setError(b?.detail ?? `Errore ${res.status}`);
    }
  };

  const eliminaRata = async (rid: string) => {
    if (!id) return;
    await fetch(`/api/v1/ordini-vendita/${id}/rate/${rid}`, { method: 'DELETE', headers: auth() });
    await caricaRate();
  };

  useEffect(() => {
    void caricaRate();
    void caricaAllegati();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleGeneraDDT = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/ddt/da-ordine-vendita/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/ddt/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione DDT');
    } finally {
      setBusy(false);
    }
  };

  const handleGeneraProforma = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/documenti-commerciali/da-ordine-vendita/${id}?tipo=proforma`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/documenti-commerciali/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione proforma');
    } finally {
      setBusy(false);
    }
  };

  const handleGeneraFattura = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/ordini-vendita/${id}/genera-fattura`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/documentale/fatture/${data.fattura_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione fattura');
    } finally {
      setBusy(false);
    }
  };

  const handleGeneraAcconto = async (rataId: string) => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/ordini-vendita/${id}/rate/${rataId}/genera-acconto`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail ?? `Errore ${res.status}`);
      }
      const data = await res.json();
      navigate(`/documentale/fatture/${data.fattura_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore generazione acconto');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const res = await fetch(`/api/v1/ordini-vendita/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        });
        if (!res.ok) throw new Error(`Errore ${res.status}`);
        setOrdine(await res.json());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore');
      }
    })();
  }, [id]);

  if (error) return <p className="p-6 text-sm text-destructive">{error}</p>;
  if (!ordine) return <p className="p-6 text-sm text-muted-foreground">Caricamento...</p>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold">
          Ordine di vendita {ordine.numero}
        </h1>
        <span className="rounded bg-neutral-200 px-2 py-1 text-xs dark:bg-neutral-700">
          {ordine.stato}
        </span>
        {ordine.stato !== 'annullato' && (
          <>
            <Button size="sm" onClick={handleGeneraDDT} disabled={busy}>
              {busy ? 'Generazione...' : 'Genera DDT'}
            </Button>
            <Button size="sm" variant="secondary" onClick={handleGeneraProforma} disabled={busy}>
              {busy ? 'Generazione...' : 'Genera Proforma'}
            </Button>
            <Button size="sm" onClick={handleGeneraFattura} disabled={busy}>
              {busy ? 'Generazione...' : 'Genera Fattura'}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleRevoca}
              disabled={busy || !ordine.revocabile}
              title={ordine.motivo_non_revocabile ?? undefined}
            >
              Revoca ordine
            </Button>
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dati ordine</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            <div>
              <p className="text-muted-foreground">Data</p>
              <p>{ordine.data_ordine}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Totale</p>
              <p className="font-medium">{fmtEUR.format(Number(ordine.importo_totale))}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Pagamento</p>
              <p>{ordine.modalita_pagamento ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Tipo offerta</p>
              <p>{ordine.tipo_offerta}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-muted-foreground">Preventivo di origine</p>
              {ordine.preventivo_id ? (
                <button
                  className="text-sm text-blue-700 hover:underline"
                  onClick={() => navigate(`/vendite/preventivi/${ordine.preventivo_id}`)}
                >
                  Apri preventivo
                </button>
              ) : (
                <p>-</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Righe</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-2">Tipo</th>
                <th className="pb-2">Descrizione</th>
                <th className="pb-2">Qta</th>
                <th className="pb-2">Prezzo</th>
                <th className="pb-2">IVA%</th>
                <th className="pb-2">Importo</th>
              </tr>
            </thead>
            <tbody>
              {ordine.righe.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-1">
                    {r.is_servizio ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Servizio</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Prodotto</span>
                    )}
                  </td>
                  <td className="py-1">{r.descrizione}</td>
                  <td className="py-1">{String(r.quantita)}</td>
                  <td className="py-1">{fmtEUR.format(Number(r.prezzo_unitario))}</td>
                  <td className="py-1">{r.iva_perc != null ? `${r.iva_perc}%` : '-'}</td>
                  <td className="py-1">{fmtEUR.format(Number(r.importo_riga))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ordine firmato dal cliente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".pdf,.p7m,.jpg,.jpeg,.png"
              disabled={uploadingFirmato}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadFirmato(f);
                e.target.value = '';
              }}
              className="text-sm"
            />
            {uploadingFirmato && (
              <span className="text-sm text-muted-foreground">Caricamento...</span>
            )}
          </div>
          {allegati.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun ordine firmato allegato.</p>
          ) : (
            <ul className="divide-y text-sm">
              {allegati.map((a) => (
                <li key={a.documento_id} className="flex items-center justify-between py-2">
                  <span>{a.nome ?? 'documento'}</span>
                  <button
                    className="text-sm text-blue-700 hover:underline"
                    onClick={() => void downloadAllegato(a.documento_id, a.year, a.nome)}
                  >
                    Scarica
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">
            Formati ammessi: PDF, P7M (firma digitale), JPG, PNG. Il file e' conservato in
            archivio come documento riservato e non viene mai interpretato.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Piano rate (scadenze alla fatturazione)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-2">#</th>
                <th className="pb-2">Tipo</th>
                <th className="pb-2">%</th>
                <th className="pb-2">Importo</th>
                <th className="pb-2">Giorni</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {rate.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-1">{r.numero}</td>
                  <td className="py-1">{r.tipo}</td>
                  <td className="py-1">{r.percentuale != null ? `${r.percentuale}%` : '-'}</td>
                  <td className="py-1">{r.importo != null ? fmtEUR.format(Number(r.importo)) : '-'}</td>
                  <td className="py-1">{r.giorni_scadenza} gg</td>
                  <td className="py-1">
                    {r.fattura_id ? (
                      <button
                        className="text-sm text-primary underline"
                        onClick={() => navigate(`/documentale/fatture/${r.fattura_id}`)}
                      >
                        Fatturato (acconto)
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        {r.tipo === 'acconto' && (
                          <button
                            className="text-sm text-primary underline disabled:opacity-50"
                            disabled={busy || ordine.stato === 'annullato'}
                            onClick={() => handleGeneraAcconto(r.id)}
                          >
                            Genera acconto
                          </button>
                        )}
                        <button className="text-sm text-destructive" onClick={() => eliminaRata(r.id)}>X</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {rate.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-2 text-muted-foreground">
                    Nessun piano rate: alla fatturazione non verranno generate scadenze automatiche.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="grid grid-cols-2 items-end gap-2 md:grid-cols-5">
            <div>
              <Label>Tipo</Label>
              <select
                className="w-full rounded border bg-transparent px-2 py-2 text-sm"
                value={nuovaRata.tipo}
                onChange={(e) => setNuovaRata({ ...nuovaRata, tipo: e.target.value })}
              >
                <option value="acconto">Acconto</option>
                <option value="saldo">Saldo</option>
                <option value="rata">Rata</option>
              </select>
            </div>
            <div>
              <Label>Percentuale</Label>
              <Input
                type="number"
                value={nuovaRata.percentuale}
                onChange={(e) => setNuovaRata({ ...nuovaRata, percentuale: e.target.value, importo: '' })}
                placeholder="% del totale"
              />
            </div>
            <div>
              <Label>oppure Importo</Label>
              <Input
                type="number"
                value={nuovaRata.importo}
                onChange={(e) => setNuovaRata({ ...nuovaRata, importo: e.target.value, percentuale: '' })}
                placeholder="importo fisso"
              />
            </div>
            <div>
              <Label>Giorni scadenza</Label>
              <Input
                type="number"
                value={nuovaRata.giorni_scadenza}
                onChange={(e) => setNuovaRata({ ...nuovaRata, giorni_scadenza: e.target.value })}
                placeholder="gg dalla fattura"
              />
            </div>
            <div>
              <Button size="sm" onClick={aggiungiRata}>+ Aggiungi rata</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default OrdineVenditaDetail;
